import { useGuestRegistryTelemetry } from '@apps/registry/guest/GuestRegistry.telemetry';
import { useUpdateRegistryNoteMutation } from '@graphql/mutations/registry.graphql.generated';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useUpdateRegistryPageContainerNoteMutation } from './PageNote.graphql.generated';

export interface PageNoteFields extends Readonly<{ note: string }> {}

export const usePageNoteController = (
  eventHandle: string,
  pageNote?: string | null,
  registryPageId?: string,
  onEditPageNote?: () => void,
  // PRNT-4193 — when set, the migrated registry exists and writes route through
  // `updatePageContainer` instead of `updateEventPage` on the legacy registry row.
  migratedPageContainerId?: string,
  // Used for cache eviction after migrated saves — `GetRegistryOnboardingSuggestions`
  // reads the synthetic registry note via `eventById(id).registryPageInfo` rather
  // than `eventByName(name)`, so we need to invalidate both root fields.
  eventId?: string,
) => {
  const [storedPageNote, setStoredPageNote] = useState(pageNote);
  const { registryNoteSet } = useGuestRegistryTelemetry();

  // No `refetchQueries`: Apollo's normalized cache propagates the mutation result
  // (`EventPage.pageSubTitleV0918` for legacy, `PageContainer.note` for migrated)
  // to anything observing the same entity. `GetRegistryOnboardingSuggestions`
  // reads `EventPage.pageSubTitleV0918` and re-renders automatically on the
  // legacy path.
  const [updateNote, { loading: legacyUpdateLoading }] = useUpdateRegistryNoteMutation();
  const [updateContainerNote, { loading: containerUpdateLoading }] = useUpdateRegistryPageContainerNoteMutation();
  const pageNoteUpdateLoading = legacyUpdateLoading || containerUpdateLoading;
  const [updateSuccess, setUpdateSuccess] = useState(false);

  const updateLegacyPageNote = async (id: string, value: string): Promise<boolean> => {
    try {
      const { data } = await updateNote({ variables: { id, payload: { pageSubTitle: value } } });
      // Presence check, not truthy — clearing the note returns
      // `pageSubTitleV0918: ''`, which is falsy but still a successful save.
      if (data?.updateEventPage) {
        setUpdateSuccess(true);
        return true;
      }
    } catch {
      // Network error — fall through to return false.
    }
    return false;
  };

  const updateMigratedPageNote = async (id: string, value: string): Promise<boolean> => {
    try {
      const { data } = await updateContainerNote({
        variables: { payload: { id, note: value } },
        update: cache => {
          // The synthetic registry-links EventPage's `pageSubTitleV0918` is
          // BE-derived from `PageContainer.note`; Apollo can't track that link,
          // so the cached `subTitle` on the synthetic row stays stale after a
          // migrated save. Evict both ROOT_QUERY entry points so consumers
          // refetch the synthetic-row value:
          //   • `eventByName(name)` — guest registry header, manage screen
          //   • `eventById(id)` — `GetRegistryOnboardingSuggestions` reads
          //     `registryPageInfo.page.pageSubtitle` from this root.
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'eventByName', args: { name: eventHandle } });
          if (eventId) {
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'eventById', args: { id: eventId } });
          }
          cache.gc();
        },
      });
      if (data?.updatePageContainer) {
        setUpdateSuccess(true);
        return true;
      }
    } catch {
      // Network error — fall through to return false.
    }
    return false;
  };

  const pageNoteFormik = useFormik({
    initialValues: {
      note: storedPageNote || '',
    },
    onSubmit: _values => {},
  });

  const updateRegistryNote = async () => {
    const pageNoteTrimmed = pageNoteFormik.values.note.trim();
    let success = false;
    if (migratedPageContainerId) {
      success = await updateMigratedPageNote(migratedPageContainerId, pageNoteTrimmed);
    } else if (registryPageId) {
      success = await updateLegacyPageNote(registryPageId, pageNoteTrimmed);
    } else {
      return;
    }
    if (success) {
      setStoredPageNote(pageNoteTrimmed);
      onEditPageNote?.();
      registryNoteSet(pageNoteTrimmed);
    }
  };

  return { updateRegistryNote, pageNoteIsDisabled: !pageNoteFormik.dirty, pageNoteFormik, pageNoteUpdateLoading, updateSuccess };
};
